import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'xp';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  icon?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  showXPToast: (amount: number, reason?: string) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration = 3000) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const toast: Toast = { id, type, message, duration };
    
    setToasts(prev => [...prev, toast]);
    
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const showXPToast = useCallback((amount: number, reason?: string) => {
    const message = reason ? `+${amount} XP \u2728 ${reason}` : `+${amount} XP`;
    showToast(message, 'xp', 2000);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showXPToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

const ToastContainer: React.FC<{ 
  toasts: Toast[]; 
  removeToast: (id: string) => void;
}> = ({ toasts, removeToast }) => {
  return (
    <div 
      className="fixed top-20 right-4 z-[1700] flex flex-col gap-3 pointer-events-none max-w-md w-full"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence>
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </AnimatePresence>
    </div>
  );
};

const ToastItem: React.FC<{ 
  toast: Toast; 
  onClose: () => void;
}> = ({ toast, onClose }) => {
  const config = getToastConfig(toast.type);

  return (
    <motion.div
      initial={{ opacity: 0, x: 100, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.95 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="pointer-events-auto"
      role="alert"
    >
      <div
        className={`
          relative overflow-hidden rounded-2xl shadow-2xl backdrop-blur-xl
          ${config.background} ${config.border}
          flex items-center gap-3 p-4 pr-12
          hover:scale-[1.02] transition-transform cursor-pointer
        `}
        onClick={onClose}
      >
        <div className={`
          flex items-center justify-center w-10 h-10 rounded-xl
          ${config.iconBg} flex-shrink-0
        `}>
          <span className="text-2xl">{config.icon}</span>
        </div>

        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm ${config.text}`}>
            {toast.message}
          </p>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className={`
            absolute top-3 right-3 w-6 h-6 rounded-lg
            flex items-center justify-center
            ${config.closeButton}
            transition-colors
          `}
          aria-label="Close notification"
        >
          {'\u2715'}
        </button>

        {toast.duration && toast.duration > 0 && (
          <motion.div
            className={`absolute bottom-0 left-0 h-1 ${config.progressBar}`}
            initial={{ width: '100%' }}
            animate={{ width: '0%' }}
            transition={{ duration: toast.duration / 1000, ease: 'linear' }}
          />
        )}
      </div>
    </motion.div>
  );
};

function getToastConfig(type: ToastType) {
  const configs = {
    success: {
      background: 'bg-green-50/90 border-green-200',
      border: 'border-2',
      iconBg: 'bg-green-500',
      icon: '\u2714',
      text: 'text-green-800',
      closeButton: 'hover:bg-green-100 text-green-600',
      progressBar: 'bg-green-500',
    },
    error: {
      background: 'bg-red-50/90 border-red-200',
      border: 'border-2',
      iconBg: 'bg-red-500',
      icon: '\u2715',
      text: 'text-red-800',
      closeButton: 'hover:bg-red-100 text-red-600',
      progressBar: 'bg-red-500',
    },
    warning: {
      background: 'bg-orange-50/90 border-orange-200',
      border: 'border-2',
      iconBg: 'bg-orange-500',
      icon: '\u26A0',
      text: 'text-orange-800',
      closeButton: 'hover:bg-orange-100 text-orange-600',
      progressBar: 'bg-orange-500',
    },
    info: {
      background: 'bg-blue-50/90 border-blue-200',
      border: 'border-2',
      iconBg: 'bg-blue-500',
      icon: '\u2139',
      text: 'text-blue-800',
      closeButton: 'hover:bg-blue-100 text-blue-600',
      progressBar: 'bg-blue-500',
    },
    xp: {
      background: 'bg-gradient-to-r from-purple-500/90 to-pink-500/90 border-purple-300',
      border: 'border-2',
      iconBg: 'bg-white/20',
      icon: '\u26A1',
      text: 'text-white font-bold',
      closeButton: 'hover:bg-white/20 text-white',
      progressBar: 'bg-white',
    },
  };

  return configs[type];
}

export default Toast;
